import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../../hooks/useAuth'
import TodoListView from '../todolist/TodoListView'
import CampaignsView from '../campaigns/CampaignsView'
import CampaignBuilderView from '../campaign-builder/CampaignBuilderView'
import KBCurationView from '../kb-curation/KBCurationView'
import ChatsViewContent from './ChatsViewContent'
import SyncAdminView from '../sync-admin/SyncAdminView'
import RescheduleConfigPanel from '../sync-admin/RescheduleConfigPanel'
import TicketsAdminView from '../discount-tickets/TicketsAdminView'
import CampaignCategoriesPanel from '../campaigns/CampaignCategoriesPanel'
import IncomingNotifications from './IncomingNotifications'
import UpcomingPanel from '../upcoming/UpcomingPanel'
import QuotationsView from '../quotations/QuotationsView'
import AnalyticsView from '../analytics/AnalyticsView'
import ChangeHistoryModal from '../change-history/ChangeHistoryModal'
import { todolistAPI } from '../../services/api'
import useThemeStore from '../../store/useThemeStore'
import NotificationSettings from '../notifications/NotificationSettings'
import PreFollowupModal from '../notifications/PreFollowupModal'
import NotificationModal from '../notifications/NotificationModal'
import useCRMStore from '../../store/useCRMStore'
import useNotifications from '../../hooks/useNotifications'
import { notificationAPI } from '../../services/api'

const STORAGE_KEY = 'crm_active_view'
const VALID_VIEWS = ['chat', 'todolist', 'campaigns', 'campaign-builder', 'kb-curation', 'sync-admin', 'quotations', 'analytics', 'reschedule-config', 'campaign-categories', 'discount-tickets']

// Leer vista guardada de localStorage
const getSavedView = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved && VALID_VIEWS.includes(saved) ? saved : 'chat'
  } catch {
    return 'chat'
  }
}

export default function Dashboard() {
  const { user, logout } = useAuth()
  const { preference, cycleTheme } = useThemeStore()
  const [activeView, setActiveView] = useState(getSavedView)
  const [isUpcomingOpen, setIsUpcomingOpen] = useState(false)
  const [showNotifSettings, setShowNotifSettings] = useState(false)
  const {
    notifications: activeNotifications,
    completeNotification,
    snoozeNotification,
    dismissNotification,
    acknowledgeNotification,
  } = useNotifications()
  const setFocusedContactPhone = useCRMStore(s => s.setFocusedContactPhone)
  const pendingDrilldown = useCRMStore(s => s.pendingDrilldown)
  const clearPendingDrilldown = useCRMStore(s => s.clearPendingDrilldown)
  const [notifConfig, setNotifConfig] = useState(null)
  const [notifPermission, setNotifPermission] = useState('default')
  const [upcomingCount, setUpcomingCount] = useState(0)
  const [showChangeHistory, setShowChangeHistory] = useState(false)

  // Cargar config de notificaciones y permisos del navegador
  useEffect(() => {
    notificationAPI.getConfig()
      .then(cfg => setNotifConfig(cfg))
      .catch(() => {})
    if ('Notification' in window) {
      setNotifPermission(Notification.permission)
    }
  }, [])

  // Redirigir agentes no-admin desde chat a todolist (vista chats es solo admin)
  useEffect(() => {
    if (user && !user.is_admin && activeView === 'chat') {
      handleViewChange('todolist')
    }
  }, [user?.is_admin, activeView])

  // Drill-down cross-component: ScheduledHeatmapTab (u otro) pone pendingDrilldown en el store
  // Dashboard lo consume y hace handleViewChange al view pedido
  useEffect(() => {
    if (pendingDrilldown?.view && VALID_VIEWS.includes(pendingDrilldown.view)) {
      handleViewChange(pendingDrilldown.view)
      // NO limpiar aqui: TodoListView lo consume y llama clearPendingDrilldown tras aplicar filtros
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDrilldown?.view])

  // Handler que guarda en localStorage al cambiar vista
  const handleViewChange = (view) => {
    setActiveView(view)
    try {
      localStorage.setItem(STORAGE_KEY, view)
    } catch (e) {
      console.warn('No se pudo guardar vista en localStorage:', e)
    }
  }

  // Cargar conteo de vencidos + hoy para el badge
  const fetchUpcomingCount = useCallback(async () => {
    try {
      const result = await todolistAPI.getUpcoming(1)
      // Contar todos los que retorna el endpoint (vencidos + hoy)
      // El endpoint con days=1 ya filtra <= hoy + 1 día
      const urgentCount = (result.contacts || []).length
      setUpcomingCount(urgentCount)
    } catch {
      // Silencioso - el badge simplemente no se muestra
    }
  }, [])

  useEffect(() => {
    fetchUpcomingCount()
    // Refrescar cada 5 minutos
    const interval = setInterval(fetchUpcomingCount, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchUpcomingCount])

  // Refrescar badge al cerrar el panel
  const handleCloseUpcoming = () => {
    setIsUpcomingOpen(false)
    fetchUpcomingCount()
  }

  // Handler for NotificationModal "Ir al contacto"
  const handleGoToContact = (notif) => {
    if (!notif) return
    setFocusedContactPhone(notif.contacto_phone)
    handleViewChange('todolist')
    // Dismiss the modal (mark as seen but not completed)
    dismissNotification(notif.id)
  }

  // Find first urgent (exact/escalation) notification to show as modal rojo
  const urgentNotif = activeNotifications.find(n => n.notification_type !== 'pre_15min')

  // Aviso anticipado (pre_15min): heads-up ámbar SIN posponer.
  const preNotif = activeNotifications.find(n => n.notification_type === 'pre_15min')
  const handleGoToContactPre = (notif) => {
    if (!notif) return
    setFocusedContactPhone(notif.contacto_phone)
    handleViewChange('todolist')
    setShowNotifSettings(false)
    acknowledgeNotification(notif.id) // descarte local; la alerta del momento exacto salta igual
  }

  // Cuando aparece la alerta del momento (exact) de un contacto, su aviso previo
  // (pre_15min) del mismo contacto queda obsoleto → descartarlo para que no reaparezca
  // como "zombie" tras atender el exact (revisión adversarial).
  useEffect(() => {
    if (!urgentNotif) return
    activeNotifications
      .filter(n => n.notification_type === 'pre_15min' && n.contacto_phone === urgentNotif.contacto_phone)
      .forEach(n => acknowledgeNotification(n.id))
  }, [urgentNotif, activeNotifications, acknowledgeNotification])

  // --- Version check: show update banner when server restarts ---
  const [showUpdateBanner, setShowUpdateBanner] = useState(false)
  const versionRef = useRef(null)
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const res = await fetch('/api/crm-web/version')
        const data = await res.json()
        if (!versionRef.current) {
          versionRef.current = data.v
        } else if (data.v !== versionRef.current) {
          setShowUpdateBanner(true)
        }
      } catch (e) { /* ignore */ }
    }
    checkVersion()
    const interval = setInterval(checkVersion, 60000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="w-screen h-screen bg-gray-50 dark:bg-surface-dark flex flex-col">
      {/* Modal de notificacion urgente (exact/escalation) */}
      <NotificationModal
        notification={urgentNotif}
        onSnooze={snoozeNotification}
        onGoToContact={handleGoToContact}
        onDismiss={dismissNotification}
      />
      {/* Aviso anticipado "Faltan 15 min" (ámbar, sin posponer; solo si no hay alerta del momento activa) */}
      <PreFollowupModal
        notification={urgentNotif ? null : preNotif}
        onGoToContact={handleGoToContactPre}
        onAcknowledge={acknowledgeNotification}
      />
      {/* Update Banner */}
      {showUpdateBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-3 flex items-center justify-center gap-4 shadow-lg">
          <span className="text-base font-medium">Nueva version disponible</span>
          <button
            onClick={() => {
              // Hard refresh con cache-bust: agregar query param ?v=timestamp fuerza al
              // browser a re-pedir index.html (en vez de servir el cacheado), que apunta
              // al bundle nuevo. Sin esto, location.reload() respeta cache HTTP y los
              // agentes pueden quedar con el bundle anterior por minutos/horas.
              const ts = Date.now()
              const url = new URL(window.location.href)
              url.searchParams.set('v', ts)
              window.location.replace(url.toString())
            }}
            className="px-4 py-1.5 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition shadow"
          >
            Recargar
          </button>
          <button
            onClick={() => setShowUpdateBanner(false)}
            className="text-white/70 hover:text-white ml-2 text-xl leading-none"
          >
            &#x2715;
          </button>
        </div>
      )}

      {/* Header */}
      <header className="h-16 bg-white dark:bg-surface-dark-alt border-b border-gray-200 dark:border-surface-dark-border flex items-center px-6 shadow-sm relative z-40">
        {/* Logo - Izquierda */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
            CA
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Club Almassenger</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Sistema de Gestión CRM</p>
          </div>
        </div>

        {/* Notificaciones de mensajes entrantes - Centro */}
        <div className="flex-1 flex justify-center">
          <IncomingNotifications
            user={user}
            onNavigateToContact={(phone) => {
              handleViewChange('todolist')
              useCRMStore.getState().setFocusedContactPhone(phone)
            }}
          />
        </div>

        {/* Usuario - Derecha */}
        <div className="flex items-center gap-4">
          {/* Botón Historial de Cambios */}
          <button
            onClick={() => setShowChangeHistory(true)}
            className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              showChangeHistory
                ? 'bg-primary-600 text-white shadow-md shadow-primary-200'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-hover'
            }`}
            title="Historial de Cambios"
          >
            <span className="text-base">📜</span>
            <span className="hidden sm:inline">Historial</span>
          </button>

          {/* TEMP: Agenda oculto - panel desactualizado y sin uso */}
          {false && (
            <button
              onClick={() => setIsUpcomingOpen(prev => !prev)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isUpcomingOpen
                  ? 'bg-primary-600 text-white shadow-md shadow-primary-200'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-hover'
              }`}
              title="Agenda de seguimiento"
            >
              <span className="text-base">🗓️</span>
              <span className="hidden sm:inline">Agenda</span>
  
              {/* Badge de conteo */}
              {upcomingCount > 0 && !isUpcomingOpen && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full shadow-sm animate-pulse">
                  {upcomingCount > 99 ? '99+' : upcomingCount}
                </span>
              )}
            </button>
          )}

          <div className="w-px h-8 bg-gray-200 dark:bg-surface-dark-border" />

          <div className="text-right">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.name || 'Usuario'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{user?.phone || ''}</p>
          </div>
          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-300 font-semibold text-sm">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <button
            onClick={logout}
            className="ml-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-hover rounded-lg transition"
            title="Cerrar sesión"
          >
            Salir
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden">
        {/* Columna 1 - Sidebar */}
        <aside className="w-16 bg-white dark:bg-sidebar-bg border-r border-gray-200 dark:border-transparent flex flex-col items-center py-4 gap-4">
          {/* Chat View - solo visible para admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('chat')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'chat'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Vista de Chats"
            >
              <span className="text-xl">💬</span>
            </button>
          )}

          {/* TodoList View */}
          <button
            onClick={() => handleViewChange('todolist')}
            className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
              activeView === 'todolist'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
            }`}
            title="Vista ToDoList"
          >
            <span className="text-xl">📋</span>
          </button>

          {/* Analytics View */}
          <button
            onClick={() => handleViewChange('analytics')}
            className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
              activeView === 'analytics'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
            }`}
            title="Analíticas"
          >
            <span className="text-xl">📊</span>
          </button>

          {/* Campaigns View */}
          <button
            onClick={() => handleViewChange('campaigns')}
            className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
              activeView === 'campaigns'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
            }`}
            title="Campañas"
          >
            <span className="text-xl">📢</span>
          </button>

          {/* Campaign Builder - solo visible para admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('campaign-builder')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'campaign-builder'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Constructor de Campañas"
            >
              <span className="text-xl">🛠️</span>
            </button>
          )}

          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('kb-curation')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'kb-curation'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Curacion KB"
            >
              <span className="text-xl">📖</span>
            </button>
          )}

          {/* Quotations View */}
          <button
            onClick={() => handleViewChange('quotations')}
            className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
              activeView === 'quotations'
                ? 'bg-primary-500 text-white shadow-sm'
                : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
            }`}
            title="Cotizaciones"
          >
            <span className="text-xl">💰</span>
          </button>

          {/* Agendamiento inteligente (config) - solo admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('reschedule-config')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'reschedule-config'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Agendamiento inteligente (config)"
            >
              <span className="text-xl">&#10024;</span>
            </button>
          )}
          {/* Categorias de campana (config) - solo admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('campaign-categories')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'campaign-categories'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Categorias de campana"
            >
              <span className="text-xl">&#127991;</span>
            </button>
          )}
          {/* Supervision de tickets de descuento (Ticket Club) - solo admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('discount-tickets')}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'discount-tickets'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Supervision de tickets de descuento"
            >
              <span className="text-xl">&#127903;</span>
            </button>
          )}
          {/* Sync Admin - solo visible para admins */}
          {user?.is_admin && (
            <button
              onClick={() => handleViewChange('sync-admin')}
              className={`mt-auto w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                activeView === 'sync-admin'
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Admin Sincronizacion"
            >
              <span className="text-xl">⚙️</span>
            </button>
          )}

          <div className="flex-1" />

          {/* Notification Settings */}
          <div className="relative">
            <button
              onClick={() => setShowNotifSettings(!showNotifSettings)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition ${
                showNotifSettings
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400'
              }`}
              title="Mis Notificaciones"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
              {activeNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {activeNotifications.length}
                </span>
              )}
            </button>
            {showNotifSettings && (
              <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifSettings(false)} />
              <div className="absolute left-14 bottom-0 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-[70vh] overflow-y-auto">
                {/* Pending notifications */}
                {activeNotifications.length > 0 && (
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Pendientes ({activeNotifications.length})</span>
                    </div>
                    {activeNotifications.map(notif => {
                      const name = notif.first_name ? (notif.first_name + ' ' + (notif.last_name || '')).trim() : (notif.nombre || notif.contacto_phone);
                      const timeStr = notif.scheduled_at?.split('T')[1]?.substring(0, 5) || '';
                      const isPre = notif.notification_type === 'pre_15min';
                      return (
                        <div key={notif.id} className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={'text-xs font-bold px-1.5 py-0.5 rounded-full ' + (isPre ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800')}>
                              {isPre ? 'En 15 min' : 'AHORA'}
                            </span>
                            <span className="text-xs text-gray-400">{timeStr}</span>
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{name}</div>
                          {notif.hint && <div className="text-xs text-gray-500 truncate">{notif.hint}</div>}
                          <div className="flex gap-2 mt-2">
                            {isPre ? (
                              <>
                                {/* pre_15min: sin posponer (el posponer vive en la alerta del momento) */}
                                <button onClick={() => acknowledgeNotification(notif.id)} className="flex-1 px-2 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300">Entendido</button>
                                <button onClick={() => handleGoToContactPre(notif)} className="flex-1 px-2 py-1 text-xs font-semibold bg-amber-500 text-white rounded hover:bg-amber-600">Ir al contacto</button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => completeNotification(notif.id)} className="flex-1 px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded hover:bg-green-600">Completar</button>
                                <button onClick={() => snoozeNotification(notif.id, 15)} title="Posponer 15 minutos" className="flex-1 px-2 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300">⏲ 15m</button>
                                <button onClick={() => snoozeNotification(notif.id, 30)} title="Posponer 30 minutos" className="flex-1 px-2 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300">⏲ 30m</button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {activeNotifications.length === 0 && (
                  <div className="px-4 py-4 text-center text-sm text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    Sin notificaciones pendientes
                  </div>
                )}
                {/* Settings */}
                {notifConfig ? (
                  <NotificationSettings
                    config={notifConfig}
                    onUpdateConfig={async (cfg) => {
                      const updated = await notificationAPI.updateConfig(cfg);
                      setNotifConfig(updated);
                      return updated;
                    }}
                    permissionState={notifPermission}
                  />
                ) : (
                  <div className="p-4 text-sm text-gray-500">Cargando configuracion...</div>
                )}
              </div>
              </>
            )}
          </div>

          <button
            onClick={cycleTheme}
            className="w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer transition hover:bg-gray-100 dark:hover:bg-sidebar-hover text-gray-500 dark:text-gray-400"
            title={preference === 'auto' ? 'Tema: Automático' : preference === 'light' ? 'Tema: Claro' : 'Tema: Oscuro'}
          >
            {preference === 'auto' ? '💻' : preference === 'light' ? '☀️' : '🌙'}
          </button>
        </aside>

        {/* Contenido según vista activa */}
        {activeView === 'chat' && user?.is_admin && <ChatsViewContent />}

        {/* Vista ToDoList */}
        {activeView === 'todolist' && <TodoListView />}

        {/* Vista Campañas */}
        {activeView === 'campaigns' && <CampaignsView />}

        {/* Vista Constructor de Campañas (solo admin) */}
        {activeView === 'campaign-builder' && user?.is_admin && <CampaignBuilderView user={user} />}

        {/* Vista Curacion KB (solo admin) */}
        {activeView === 'kb-curation' && user?.is_admin && <KBCurationView />}

        {/* Vista Sync Admin (solo admin) */}
        {activeView === 'sync-admin' && user?.is_admin && <SyncAdminView />}
        {activeView === 'reschedule-config' && user?.is_admin && <RescheduleConfigPanel />}
        {activeView === 'discount-tickets' && user?.is_admin && <TicketsAdminView />}
        {activeView === 'campaign-categories' && user?.is_admin && <CampaignCategoriesPanel />}

        {/* Vista Cotizaciones */}
        {activeView === 'quotations' && <QuotationsView />}

        {/* Vista Analíticas */}
        {activeView === 'analytics' && <AnalyticsView />}

      </main>

      {/* Modal Historial de Cambios */}
      {showChangeHistory && (
        <ChangeHistoryModal
          onClose={() => setShowChangeHistory(false)}
          onNavigateToContact={(phone) => {
            setShowChangeHistory(false);
            handleViewChange('todolist');
            useCRMStore.getState().setFocusedContactPhone(phone);
          }}
        />
      )}

      {/* Panel Agenda (overlay fixed, fuera del flow) */}
      <UpcomingPanel isOpen={isUpcomingOpen} onClose={handleCloseUpcoming} />
    </div>
  )
}
